/**
 * Centralised gtag (Google Ads / Analytics) event helpers.
 *
 * Consent Mode v2 is initialised in /public/index.html with all storage
 * categories DENIED by default. The CookieConsent banner calls
 * `updateConsent({ analytics: true, ads: true })` once the user opts in.
 *
 * All event functions here are no-ops if window.gtag is missing (e.g.
 * during SSR / tests / ad-blockers) so callers never need to guard.
 */
import { updateMetaConsent } from "./meta";

const CONSENT_KEY = "pg_consent_v1"; // {"analytics":bool,"ads":bool,"ts":number}

export function readConsent() {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function persistConsent(state) {
  try {
    localStorage.setItem(CONSENT_KEY, JSON.stringify({ ...state, ts: Date.now() }));
  } catch {}
}

/** Push a Consent Mode v2 update to gtag AND mirror it to the Meta
 *  Pixel consent API so one banner choice toggles both ad platforms. */
export function updateConsent({ analytics, ads }) {
  if (typeof window !== "undefined" && typeof window.gtag === "function") {
    window.gtag("consent", "update", {
      ad_storage: ads ? "granted" : "denied",
      ad_user_data: ads ? "granted" : "denied",
      ad_personalization: ads ? "granted" : "denied",
      analytics_storage: analytics ? "granted" : "denied",
    });
  }
  // Meta Pixel: only fires when ads consent granted.
  updateMetaConsent({ ads });
}

/** Apply previously stored consent at app boot so subsequent events respect it. */
export function applyStoredConsent() {
  const stored = readConsent();
  if (stored) updateConsent({ analytics: !!stored.analytics, ads: !!stored.ads });
}

function fire(name, params) {
  if (typeof window === "undefined" || typeof window.gtag !== "function") return;
  window.gtag("event", name, params || {});
}

export const track = {
  /** Product page view (PDP). */
  viewItem(product) {
    if (!product) return;
    fire("view_item", {
      currency: "GBP",
      value: Number(product.price || 0),
      items: [{
        item_id: product.sku,
        item_name: product.name,
        item_variant: product.strength || "",
        item_category: product.category || "",
        price: Number(product.price || 0),
        quantity: 1,
      }],
    });
  },

  /** User added a product to the basket. */
  addToCart(product, quantity = 1) {
    if (!product) return;
    fire("add_to_cart", {
      currency: "GBP",
      value: Number(product.price || 0) * quantity,
      items: [{
        item_id: product.sku,
        item_name: product.name,
        item_variant: product.strength || "",
        item_category: product.category || "",
        price: Number(product.price || 0),
        quantity,
      }],
    });
  },

  /** Cart page viewed. */
  viewCart(items, subtotal) {
    fire("view_cart", {
      currency: "GBP",
      value: Number(subtotal || 0),
      items: (items || []).map((i) => ({
        item_id: i.sku,
        item_name: i.name,
        item_variant: i.strength || "",
        price: Number(i.price || 0),
        quantity: i.quantity,
      })),
    });
  },

  /** Reached /checkout. */
  beginCheckout(items, subtotal) {
    fire("begin_checkout", {
      currency: "GBP",
      value: Number(subtotal || 0),
      items: (items || []).map((i) => ({
        item_id: i.sku,
        item_name: i.name,
        item_variant: i.strength || "",
        price: Number(i.price || 0),
        quantity: i.quantity,
      })),
    });
  },

  /** Newsletter / lead-magnet signup. */
  signUpNewsletter() {
    fire("sign_up", { method: "newsletter" });
  },

  /** Affiliate-programme application. */
  signUpAffiliate() {
    fire("sign_up", { method: "affiliate" });
  },

  /** Customer account creation. */
  signUpAccount() {
    fire("sign_up", { method: "account" });
  },
};
